import { useMemo } from "react";
import { HeatmapCircle, HeatmapRect as VisxHeatmapRect } from "@visx/heatmap";
import type {
  HeatmapRectComponentProps as VisxHeatmapRectComponentProps,
  RectCell,
  CircleCell,
} from "@visx/heatmap";
import { scaleLinear, scaleSqrt } from "@visx/scale";
import { useChartColors, type ChartColorToken } from "./colors";
import type { Closed } from "./internal/strip";
import { omitUndefined } from "../../lib/props";
import type { Omittable } from "../../lib/props";

/**
 * Heatmaps (`@visx/heatmap`). Color is driven exclusively by
 * `colorScale`/`opacityScale` (there is no `fill` prop — closed by construction);
 * build one with {@link useHeatmapColorScale} (sequential single-hue). Pass `data`
 * (columns of bins) + `xScale`/`yScale` index→pixel functions + `bins`/`count`.
 */
export { HeatmapCircle };
export type { RectCell, CircleCell };

export type HeatmapRectProps<ColumnDatum, BinDatum> = Closed<
  Omit<
    Omit<VisxHeatmapRectComponentProps<ColumnDatum, BinDatum>, "style">,
    "children" | "className" | "fill" | "fillOpacity" | "stroke" | "strokeWidth"
  >
> & Omittable<{
  /** Faint theme token used for zero-count cells, outside the data ramp. */
  zeroColorToken?: ChartColorToken;
  /** Fill opacity for zero-count cells. */
  zeroOpacity?: number;
  /** Minimum opacity for non-zero cells when an opacity scale is active. */
  minNonZeroOpacity?: number;
  /** Predicate for drawing a themed outline around selected cells. */
  isCellOutlined?: (cell: RectCell<ColumnDatum, BinDatum>) => boolean;
  /** Theme token for outlined cells. */
  outlineColorToken?: ChartColorToken;
  /** Stroke width for outlined cells. */
  outlineWidth?: number;
}>;

/**
 * A themed rectangular heatmap. Zero-count cells can stay visible via a baseline
 * token, non-zero opacity can be floored for legibility, and individual cells can
 * receive a token-colored outline without exposing raw SVG styling to consumers.
 */
export function HeatmapRect<ColumnDatum, BinDatum>({
  zeroColorToken = "muted",
  zeroOpacity = 0.16,
  minNonZeroOpacity = 0.25,
  isCellOutlined,
  outlineColorToken = "primary",
  outlineWidth = 1.5,
  colorScale,
  opacityScale,
  ...props
}: HeatmapRectProps<ColumnDatum, BinDatum>) {
  const c = useChartColors();
  // Both tokens carry a default, so both resolve to a paintable string.
  const zeroFill = c.resolve(zeroColorToken);
  const outline = c.resolve(outlineColorToken);

  return (
    <VisxHeatmapRect<ColumnDatum, BinDatum>
      {...(props as Omit<VisxHeatmapRectComponentProps<ColumnDatum, BinDatum>, "children">)}
      {...omitUndefined({ colorScale, opacityScale })}
    >
      {(cells) =>
        cells.map((column) =>
          column.map((cell) => {
            const isZero = cell.count === 0;
            const outlined = isCellOutlined?.(cell) ?? false;
            return (
              <rect
                key={`heatmap-tile-rect-${cell.row}-${cell.column}`}
                width={cell.width}
                height={cell.height}
                x={cell.x}
                y={cell.y}
                rx={2}
                fill={isZero ? zeroFill : cell.color}
                fillOpacity={
                  // `?? 1` is a backstop: visx's own default `opacityScale` is
                  // `() => 1`, so a cell always arrives carrying a number.
                  isZero ? zeroOpacity : Math.max(cell.opacity ?? 1, minNonZeroOpacity)
                }
                stroke={outlined ? outline : undefined}
                strokeWidth={outlined ? outlineWidth : undefined}
              />
            );
          }),
        )
      }
    </VisxHeatmapRect>
  );
}

/**
 * THE sequential `count → color` scale for a heatmap's `colorScale`. Sequential
 * means ONE hue, light→saturated — magnitude is a single quantity, so it never
 * borrows the categorical ramp (two ramp hues interpolated read as two
 * different data classes). The hue defaults to `primary`; the low end is a
 * near-surface tint of the same hue so small counts stay on-hue, not grey.
 * `shape` matches the data's skew (`sqrt` lifts low counts); `constant` pins
 * every non-zero cell to the full hue for opacity-only encodings.
 */
export function useHeatmapColorScale(
  max: number,
  {
    shape = "linear",
    colorToken = "primary",
  }: { shape?: "linear" | "sqrt" | "constant"; colorToken?: ChartColorToken } = {},
): HeatmapColorScale {
  const c = useChartColors();
  return useMemo(() => {
    const hue = c.paint(colorToken);
    if (shape === "constant") return () => hue;

    // What varies across the ramp is a MIX PERCENTAGE, not a color.
    //
    // A token is a `var()`, and a `var()` has no value until the cascade
    // answers it — so no JS interpolator can walk from one to another. d3
    // handed two unparseable strings falls back to interpolating the numbers
    // inside them, which for `var(--primary)` is no numbers at all: every
    // non-zero cell then paints the identical full hue and the encoding goes
    // silently flat. Interpolating the percentage instead keeps the whole
    // gradient in CSS, where `color-mix` resolves it live and re-resolves it
    // on a theme flip.
    const domain = [0, max || 1];
    const percent = shape === "sqrt"
      ? scaleSqrt<number>({ domain, range: [MIN_MIX_PERCENT, 100] })
      : scaleLinear<number>({ domain, range: [MIN_MIX_PERCENT, 100] });
    return (count) =>
      `color-mix(in oklab, ${hue} ${percent(Number(count))}%, ${c.card})`;
  }, [max, shape, colorToken, c]);
}

/**
 * `count → CSS color`. Matches what visx's `colorScale` prop accepts: d3 scales
 * take anything coercible, so a bin count arrives as `number` or as a boxed
 * `valueOf`.
 */
export type HeatmapColorScale = (count: number | { valueOf(): number }) => string;

/** Floor of the sequential mix, so the smallest count still reads as the hue
 *  laid faintly over the card rather than as the card itself. */
const MIN_MIX_PERCENT = 25;

/** A `count → opacity` scale for a heatmap's `opacityScale`. */
export function useHeatmapOpacityScale(max: number) {
  return useMemo(
    () => scaleLinear<number>({ domain: [0, max], range: [0.15, 1] }),
    [max],
  );
}
